import type { ReactElement } from 'react';

/**
 * The four theme-safe paints a pictogram is drawn with. `ink` mixes the
 * category colour toward `--text`, so it darkens on the light theme and
 * lightens on the dark one and never sinks into the tile; `pale` mixes toward
 * `--surface` for the plate a scene sits on; `paper` is the knockout.
 */
export interface Tones {
  ink: string;
  mid: string;
  pale: string;
  paper: string;
  good: string;
  check: string;
}

const S = 1.8;
const stroke = (fill: string, width = S) => ({
  fill: 'none',
  stroke: fill,
  strokeWidth: width,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

const Lens = ({ t }: { t: Tones }) => (
  <>
    <circle cx="17.6" cy="16.8" r="4" {...stroke(t.ink, 1.9)} fill={t.paper} />
    <path d="M20.5 19.7 L22.6 21.8" {...stroke(t.ink, 2.1)} />
  </>
);

/** A four-point star, used as the "we added something" mark on enrichment. */
const Sparkle = ({ t, cx = 18.6, cy = 7.4 }: { t: Tones; cx?: number; cy?: number }) => (
  <path
    d={`M${cx} ${cy - 4.6} L${cx + 1.4} ${cy - 1.4} L${cx + 4.6} ${cy} L${cx + 1.4} ${cy + 1.4} L${cx} ${cy + 4.6} L${cx - 1.4} ${cy + 1.4} L${cx - 4.6} ${cy} L${cx - 1.4} ${cy - 1.4} Z`}
    fill={t.mid}
  />
);

const Envelope = ({ t }: { t: Tones }) => (
  <>
    <rect x="1.6" y="4.6" width="17" height="12.4" rx="2.4" fill={t.pale} />
    <path d="M2.6 6 L10.1 11.6 L17.6 6" {...stroke(t.ink, 2)} />
    <rect x="4.6" y="13.4" width="8" height="2" rx="1" fill={t.mid} />
  </>
);

const Building = ({ t }: { t: Tones }) => (
  <>
    <rect x="2.8" y="2.4" width="13.8" height="19" rx="2.2" fill={t.pale} />
    <path
      d="M5 5.2h3.2v2.8H5Z M10.4 5.2h3.2v2.8h-3.2Z M5 9.6h3.2v2.8H5Z M10.4 9.6h3.2v2.8h-3.2Z"
      fill={t.ink}
    />
    <rect x="7.9" y="15.8" width="3.6" height="5.6" rx="1" fill={t.mid} />
  </>
);

const Contact = ({ t }: { t: Tones }) => (
  <>
    <rect x="1.4" y="3.6" width="16.2" height="14.8" rx="2.6" fill={t.pale} />
    <circle cx="6.4" cy="9.2" r="2.6" fill={t.ink} />
    <path d="M2.9 15.6a3.5 3.5 0 0 1 7 0Z" fill={t.ink} />
    <path d="M11.4 7.4h4.4v2h-4.4Z M11.4 11.2h3v2h-3Z" fill={t.mid} />
  </>
);

/** One mini pictogram of the artifact a DIY platform returns, per catalog id. */
export const DIY_SCENES: Record<string, (t: Tones) => ReactElement> = {
  seo: (t) => (
    <>
      <rect x="2" y="2" width="20" height="20" rx="3" fill={t.pale} />
      <rect x="4.4" y="4.4" width="15.2" height="3.4" rx="1.7" fill={t.paper} />
      <circle cx="17.4" cy="6.1" r="1.5" fill={t.ink} />
      <rect x="4.4" y="10" width="15.2" height="2.8" rx="1.4" fill={t.ink} />
      <path d="M4.4 14.6h12v2.4h-12Z M4.4 18.2h8.6v2.4H4.4Z" fill={t.mid} />
    </>
  ),
  web: (t) => (
    <>
      <rect x="1.8" y="3.6" width="20.4" height="16.8" rx="2.6" fill={t.pale} />
      <rect x="3.4" y="8.6" width="17.2" height="10.2" rx="1.6" fill={t.paper} />
      <circle cx="5.6" cy="6.1" r="1" fill={t.ink} />
      <circle cx="9" cy="6.1" r="1" fill={t.ink} />
      <path
        d="M10.2 10.4 L8.6 10.4 L8.6 13.1 L7 13.9 L8.6 14.7 L8.6 17.4 L10.2 17.4M13.8 10.4 L15.4 10.4 L15.4 13.1 L17 13.9 L15.4 14.7 L15.4 17.4 L13.8 17.4"
        {...stroke(t.ink, 1.9)}
      />
    </>
  ),
  email: (t) => (
    <>
      <rect x="1.8" y="4.4" width="20.4" height="15.2" rx="2.6" fill={t.pale} />
      <path d="M3 6 L12 12.8 L21 6" {...stroke(t.ink, 2.1)} />
      <rect x="6" y="15.4" width="12" height="2.4" rx="1.2" fill={t.mid} />
    </>
  ),
  'email-finding': (t) => (
    <>
      <Envelope t={t} />
      <Lens t={t} />
    </>
  ),
  'email-verification': (t) => (
    <>
      <Envelope t={t} />
      <circle cx="17.4" cy="16.6" r="5" fill={t.good} />
      <path d="M15.1 16.7 L16.8 18.4 L19.8 15" {...stroke(t.check, 2.2)} />
    </>
  ),
  'mobile-phone': (t) => (
    <>
      <rect x="5.6" y="1.8" width="12.8" height="20.4" rx="3.2" fill={t.ink} />
      <rect x="7.4" y="4.4" width="9.2" height="15.2" rx="1.6" fill={t.paper} />
      <path d="M8.8 7.6h6.4v2.2H8.8Z M8.8 11.4h6.4v2.2H8.8Z" fill={t.mid} />
      <rect x="10" y="15.4" width="4" height="2.2" rx="1.1" fill={t.pale} />
    </>
  ),
  person: (t) => <Contact t={t} />,
  'person-enrichment': (t) => (
    <>
      <Contact t={t} />
      <Sparkle t={t} />
    </>
  ),
  'people-search': (t) => (
    <>
      <Contact t={t} />
      <Lens t={t} />
    </>
  ),
  company: (t) => <Building t={t} />,
  'company-search': (t) => (
    <>
      <Building t={t} />
      <Lens t={t} />
    </>
  ),
  'company-enrichment': (t) => (
    <>
      <Building t={t} />
      <Sparkle t={t} cx={18.4} cy={17.4} />
    </>
  ),
  technographics: (t) => (
    <>
      <rect x="2.4" y="2.4" width="8.8" height="8.8" rx="2.4" fill={t.ink} />
      <rect x="12.8" y="2.4" width="8.8" height="8.8" rx="2.4" fill={t.mid} />
      <rect x="2.4" y="12.8" width="8.8" height="8.8" rx="2.4" fill={t.mid} />
      <rect x="12.8" y="12.8" width="8.8" height="8.8" rx="2.4" fill={t.pale} />
    </>
  ),
  'job-search': (t) => (
    <>
      <path d="M9 5.4V4.2a1.6 1.6 0 0 1 1.6-1.6h2.8a1.6 1.6 0 0 1 1.6 1.6v1.2" {...stroke(t.ink, 2)} />
      <rect x="2.2" y="5.4" width="19.6" height="14.2" rx="2.6" fill={t.pale} />
      <rect x="2.2" y="10.4" width="19.6" height="2.8" fill={t.ink} />
      <rect x="10.2" y="9.6" width="3.6" height="4.4" rx="1.2" fill={t.mid} />
    </>
  ),
  social: (t) => (
    <>
      <rect x="1.8" y="4" width="20.4" height="16" rx="2.8" fill={t.pale} />
      <circle cx="7" cy="10.2" r="2.6" fill={t.ink} />
      <circle cx="12" cy="10.2" r="2.6" fill={t.mid} />
      <circle cx="17" cy="10.2" r="2.6" fill={t.ink} />
      <rect x="6" y="15.2" width="12" height="2.4" rx="1.2" fill={t.mid} />
    </>
  ),
  congress: (t) => (
    <>
      <path d="M5.4 10.4 L9.6 6.2 L12.8 9 L18 3.8" {...stroke(t.ink, 2.2)} />
      <path d="M14.4 3.8h3.8v3.8" {...stroke(t.ink, 2.2)} />
      <path d="M4 18.6a8 8 0 0 1 16 0Z" fill={t.pale} />
      <rect x="1.8" y="18.4" width="20.4" height="3" rx="1.2" fill={t.ink} />
    </>
  ),
  sec: (t) => (
    <>
      <rect x="3.6" y="1.8" width="14" height="18.6" rx="2.2" fill={t.pale} />
      <path d="M6 5.4h9.2v2.2H6Z M6 9.2h9.2v2.2H6Z M6 13h5.6v2.2H6Z" fill={t.ink} />
      <circle cx="17" cy="17.4" r="4.8" fill={t.mid} />
      <circle cx="17" cy="17.4" r="2.2" fill={t.paper} />
    </>
  ),
};
